// src/render/EventBus.ts — typed Phaser EventEmitter bridging Phaser scenes
// and React layers. Mirrors the canonical phaserjs/template-react-ts pattern
// (WARNING-4 closure for Phase 6 Plan 04 — installs the bridge so
// GameScene.create() can announce readiness to React layers without going
// through renderBus, which is for sim→render data flow only).
//
// Used by:
//   - GameScene.create() emits 'current-scene-ready' at the end of create()
//     once `this.sprites` is populated and the camera follow is established.
//   - PhaserGame.tsx subscribes at module scope (idempotent under React 19
//     StrictMode double-invoke) to cache the active scene reference for
//     React layers to read sprite positions and apply camera bias.
//   - Future scene-shutdown paths (Phaser scene tear-down) emit
//     'scene-shutdown' so the cached ref clears.
//
// Distinct from src/render/bus.ts (renderBus): this bridge carries Phaser
// scene lifecycle signals; renderBus carries sim→UI event payloads.

import * as Phaser from 'phaser';

export type EventBusEventMap = {
  'current-scene-ready': (scene: Phaser.Scene) => void;
  'scene-shutdown': (scene: Phaser.Scene) => void;
};

/**
 * Phaser's EventEmitter is untyped; this is a thin typed facade so callers
 * get autocomplete + payload typing without sacrificing the wide-compat
 * Phaser primitive underneath.
 */
class TypedEventBus {
  private inner = new Phaser.Events.EventEmitter();

  /** Subscribe; returns an unsubscribe function for symmetry with renderBus. */
  on<K extends keyof EventBusEventMap>(
    event: K,
    cb: EventBusEventMap[K],
  ): () => void {
    this.inner.on(event, cb as never);
    return (): void => {
      this.inner.off(event, cb as never);
    };
  }

  emit<K extends keyof EventBusEventMap>(
    event: K,
    ...args: Parameters<EventBusEventMap[K]>
  ): void {
    this.inner.emit(event, ...args);
  }

  off<K extends keyof EventBusEventMap>(
    event: K,
    cb: EventBusEventMap[K],
  ): void {
    this.inner.off(event, cb as never);
  }
}

/** Singleton bridge — one per client. */
export const EventBus = new TypedEventBus();
